import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { RootState } from "~/app/store";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { selectCart } from "~/features/cart/cart/redux/selectors/groupedCartSelectors";
import { selectCartPageAggregates } from "~/features/cart/cart/redux/selectors/cartPageAggregates";
import { selectDisableCheckout, selectIsPnaLoading } from "~/features/cart/cart/redux/selectors/cartPageSelectors";
import { selectPackageEntryMap } from "~/features/cart/cart/redux/selectors/cartSelectors";
import {
  switchProductVersion,
  updatePriceReferenceForCart as updatePriceReferenceAction,
} from "~/features/cart/cart/redux/actions/cartPageActions";
import { deleteSectionSaga } from "~/features/cart/cart/redux/actions/cartQuoteCommonActions";
import { insertAlternateRow, closeAlternateRow } from "~/features/cart/cartV2/redux/cartAlternatesSlice";
import {
  selectOrderablesViewModel,
  selectBundleViewModels,
} from "~/features/cart/cartV2/redux/selectors/cartTableSelectors";
import { buildTableId } from "~/features/cart/cartV2/utils/buildTableId";

export interface InvalidCartItem {
  tableId: string;
  cartEntryId: number;
}

const selectInvalidItems = (state: RootState): InvalidCartItem[] => {
  const cart = selectCart(state);
  const invalidEntries: InvalidCartItem[] = [];

  const processGroup = (
    group: ReturnType<typeof selectCart>["orderables"] | ReturnType<typeof selectCart>["bundles"][number],
  ) => {
    const tableId = buildTableId(group);
    group.entries.forEach((entry) => {
      if (
        entry.packGroups.length > 0 &&
        entry.packGroups.some(
          (pack) =>
            pack.packEntryIds.length > 0 &&
            pack.packEntryIds.some((id) => {
              const lineItem = state.cart.packEntriesById[id];
              const productDescriptor = selectProductDescriptor(state, lineItem);
              return !lineItem.valid || productDescriptor.isError;
            }),
        )
      ) {
        invalidEntries.push({ tableId, cartEntryId: entry.cartEntryId });
      }
    });
  };

  processGroup(cart.orderables);
  cart.bundles.forEach(processGroup);

  return invalidEntries;
};

export function useB2BCartPageViewModel(externalDisableCheckout: boolean) {
  const dispatch = useDispatch();

  const aggregates = useSelector(selectCartPageAggregates);
  const disableCheckout = useSelector(selectDisableCheckout(externalDisableCheckout));
  const isPnaLoading = useSelector(selectIsPnaLoading);
  const cartData = useSelector(selectCart);
  const orderablesViewModel = useSelector(selectOrderablesViewModel);
  const bundleViewModels = useSelector(selectBundleViewModels);
  const invalidItems = useSelector(selectInvalidItems);
  const packageEntryMap = useSelector(selectPackageEntryMap);
  const packageEntryIds = Object.keys(packageEntryMap).map(Number);

  const orderablesTableId = orderablesViewModel.tableId;
  const orderablesEntryIds = cartData.orderables.entries.map((e) => e.cartEntryId);

  const handleInsertAlternateRow = useCallback(
    (input: { anchorCartEntryId: number | null; productCode: string; designation: string }) => {
      dispatch(insertAlternateRow({ tableId: orderablesTableId, ...input }));
    },
    [dispatch, orderablesTableId],
  );

  const handleCloseAlternateRow = useCallback(
    (rowId: string) => {
      dispatch(closeAlternateRow({ tableId: orderablesTableId, rowId, entryIds: orderablesEntryIds }));
    },
    [dispatch, orderablesTableId, orderablesEntryIds],
  );

  const handleDeleteSection = useCallback(
    (quoteNumber?: string) => {
      dispatch(deleteSectionSaga(quoteNumber ?? ""));
    },
    [dispatch],
  );

  const handleUpdatePriceReferenceForCart = useCallback(
    (priceReference: string, onSuccess: (count: number) => void) => {
      dispatch(updatePriceReferenceAction(priceReference, packageEntryIds, onSuccess));
    },
    [dispatch, packageEntryIds],
  );

  const handleSwitchProductVersionForCart = useCallback(() => {
    dispatch(switchProductVersion());
  }, [dispatch]);

  return {
    aggregates,
    disableCheckout,
    isPnaLoading,
    cartData,
    orderablesViewModel,
    bundleViewModels,
    invalidItems,
    handleInsertAlternateRow,
    handleCloseAlternateRow,
    handleDeleteSection,
    handleUpdatePriceReferenceForCart,
    handleSwitchProductVersionForCart,
  };
}
